import type { CardPaymentAcceptOptions } from '../interfaces/cardPayment.interface';

export function useCardPaymentAccept(options: CardPaymentAcceptOptions) {
  const { $paymentMethods } = useNuxtApp();
  const API = useKegelApi();
  const store = useKegelAnalyticStore();
  const { track } = useEventsDispatcher();
  const { buyUpsale } = payments();
  const { intent, timing, nameOnCard } = options;

  async function acceptPayment(paymentIntentId: string): Promise<boolean> {
    let response;
    try {
      if (!options.withEmail && options.upsale.value) {
        const upsaleError = await buyUpsale({
          intent_id: paymentIntentId,
          item: options.chargebeeId.value,
          web_user_uuid: store.userUuid,
          stage: 'upsale',
          amount: options.roundedPrice.value,
        });

        if (upsaleError) throw upsaleError;
      } else {
        response = await API.creditCardAccept({
          intent_id: paymentIntentId,
          new_flow: true,
          name: nameOnCard.value,
          item: options.chargebeeId.value,
        });
      }
    } catch (error: any) {
      options.onAcceptError(error);

      return false;
    }

    if (response?.detail.error) {
      options.onAcceptError(response.detail.error);

      return false;
    }

    intent.drop();
    options.onSuccess(paymentIntentId);

    return true;
  }

  async function onAuthorizationSuccess(): Promise<void> {
    if (options.withEmail && options.upsale.value) {
      options.onBuyUpsale?.({ intent_id: intent.paymentIntent.value?.id });

      return;
    }

    track('Authorization_Success', {
      type: $paymentMethods.card,
      price: options.discountPrice.value,
      firstname: nameOnCard.value.split(' ')[0] || undefined,
      lastname: nameOnCard.value.split(' ')[1] || undefined,
    });

    timing.startAccept();
    const isAccepted = await acceptPayment(intent.paymentIntent.value?.id);
    timing.finishAccept();

    timing.trackPaymentDuration(isAccepted ? 'success' : 'error');

    track('CC Payment Attempt');
  }

  return { onAuthorizationSuccess };
}
